import React, { useMemo, useState } from 'react';
import { Text, View } from 'react-native';
import { Image } from 'expo-image';
import { getAssetUrl, ASSET_PATHS } from '../../../../constants/assets';
import { getSportDefinitionOrDefault } from '../../sports.constants';
import type { SportKey } from '../../sports.types';
import { getSportIconStyles } from './SportIcon.styles';
import { SPORT_ICON_FALLBACK_GLYPH, SPORT_ICON_TEST_IDS } from './SportIcon.constants';

export interface SportIconProps {
  /** Sport key; anything unrecognised falls back to football. */
  sport?: string | null;
  size?: number;
  /** Hide from screen readers when the sport is already named in adjacent text. */
  decorative?: boolean;
}

const ICON_PATH_BY_SPORT: Record<SportKey, string> = {
  FOOTBALL: ASSET_PATHS.SPORT_ICONS.FOOTBALL,
  BASKETBALL: ASSET_PATHS.SPORT_ICONS.BASKETBALL,
  FORMULA_ONE: ASSET_PATHS.SPORT_ICONS.FORMULA_ONE,
};

/**
 * The sport's icon, served from the CDN (specs/multi-sport-challenges).
 *
 * Falls back to a text glyph if the image fails to load. That is not defensive padding: the
 * icons are uploaded by a separate script, so between shipping this code and running that upload
 * the CDN returns 404 — and a broken-image box in every row of the game list would read as a bug.
 * The glyph keeps the sport legible until the real icon lands.
 */
export const SportIcon: React.FC<SportIconProps> = ({ sport, size = 18, decorative = false }) => {
  const styles = useMemo(() => getSportIconStyles(size), [size]);
  const [failed, setFailed] = useState(false);

  const definition = getSportDefinitionOrDefault(sport);
  const path = ICON_PATH_BY_SPORT[definition.key];
  const accessibility = decorative
    ? { accessibilityElementsHidden: true, importantForAccessibility: 'no-hide-descendants' as const }
    : { accessibilityLabel: definition.label };

  if (failed || !path) {
    return (
      <View style={styles.fallback} testID={SPORT_ICON_TEST_IDS.fallback} {...accessibility}>
        <Text style={styles.fallbackGlyph}>
          {SPORT_ICON_FALLBACK_GLYPH[definition.key] ?? definition.label.slice(0, 1)}
        </Text>
      </View>
    );
  }

  return (
    <Image
      source={{ uri: getAssetUrl(path) }}
      style={styles.icon}
      contentFit="contain"
      transition={120}
      onError={() => setFailed(true)}
      testID={SPORT_ICON_TEST_IDS.image}
      {...accessibility}
    />
  );
};
